import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import { RecordDetailShell, recordDetailShellStyles } from '@/components/workspace/RecordDetailShell';
import { InboxReplyComposer } from '@/features/mail/InboxReplyComposer';
import {
  ReplyClassificationBadge,
  type ReplyClassificationSummary,
} from '@/features/mail/ReplyClassificationBadge';
import { extractEmailAddress } from '@marketing-hub/shared';
import leadStyles from '@/features/leads/Leads.module.css';
import projectStyles from '@/pages/ProjectsPage.module.css';

export type InboxMessageDetailData = {
  id: string;
  subject: string | null;
  from_addr: string | null;
  direction: string;
  status?: string;
  body_text: string | null;
  lead_id: string | null;
  lead_name?: string | null;
  lead_email?: string | null;
  mailbox_label?: string | null;
  mailbox_from_email?: string | null;
  smtp_account_id?: string | null;
  received_at?: string | null;
  sent_at?: string | null;
  created_at: string;
  reply_classification?: ReplyClassificationSummary | null;
};

type Props = {
  message: InboxMessageDetailData;
  linkLeadId: string;
  onLinkLeadIdChange: (value: string) => void;
  replyOpen: boolean;
  onReplyOpenChange: (open: boolean) => void;
  onClose: () => void;
  onDraftResolved?: () => void;
  patchMsg: UseMutationResult<unknown, Error, { id: string; body: Record<string, unknown> }, unknown>;
};

export function InboxMessageDetail({
  message,
  linkLeadId,
  onLinkLeadIdChange,
  replyOpen,
  onReplyOpenChange,
  onClose,
  onDraftResolved,
  patchMsg,
}: Props) {
  const isDraft = message.status === 'draft';
  const [draftSubject, setDraftSubject] = useState(message.subject ?? '');
  const [draftBody, setDraftBody] = useState(message.body_text ?? '');

  useEffect(() => {
    setDraftSubject(message.subject ?? '');
    setDraftBody(message.body_text ?? '');
  }, [message.id, message.subject, message.body_text]);

  const approveM = useMutation({
    mutationFn: () =>
      apiFetch(`/api/mail/messages/${message.id}/approve`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      onDraftResolved?.();
      onClose();
    },
  });

  const discardM = useMutation({
    mutationFn: () =>
      apiFetch(`/api/mail/messages/${message.id}/discard`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      onDraftResolved?.();
      onClose();
    },
  });

  const when = new Date(
    message.received_at ?? message.sent_at ?? message.created_at,
  ).toLocaleString('nl-NL');
  const mailbox = message.mailbox_label ?? message.mailbox_from_email ?? '—';
  const initials = (message.subject ?? 'M').slice(0, 2).toUpperCase();

  return (
    <RecordDetailShell ariaLabel="Berichtdetail">
      <RecordDetailShell.Header
        identity={
          <div className={recordDetailShellStyles.avatar} aria-hidden>
            {initials}
          </div>
        }
        title={message.subject ?? 'Bericht'}
        subtitle={`${message.from_addr ?? '—'} · ${when}`}
        badges={
          <>
            {isDraft ? (
              <span className={recordDetailShellStyles.badgeWarn}>Automation concept</span>
            ) : null}
            {message.lead_id ? (
              <Link to={`/app/leads?lead=${message.lead_id}`} className={leadStyles.metaChipLink}>
                {message.lead_name ?? message.lead_email ?? 'Lead'}
              </Link>
            ) : (
              <span className={recordDetailShellStyles.badgeMuted}>Geen lead</span>
            )}
            {message.direction === 'inbound' ? (
              <ReplyClassificationBadge classification={message.reply_classification} />
            ) : null}
          </>
        }
        onClose={onClose}
        closeLabel="Bericht sluiten"
      />

      <RecordDetailShell.Body>
        <p className={recordDetailShellStyles.metaLine}>Mailbox: {mailbox}</p>

        {isDraft ? (
          <>
            <Field label="Onderwerp">
              <TextInput value={draftSubject} onChange={(e) => setDraftSubject(e.target.value)} />
            </Field>
            <Field label="Body">
              <TextArea rows={8} value={draftBody} onChange={(e) => setDraftBody(e.target.value)} />
            </Field>
            <div className={projectStyles.row}>
              <Button
                variant="secondary"
                onClick={() =>
                  patchMsg.mutate({
                    id: message.id,
                    body: { subject: draftSubject, body_text: draftBody },
                  })
                }
              >
                Opslaan
              </Button>
              <Button variant="primary" disabled={approveM.isPending} onClick={() => approveM.mutate()}>
                {approveM.isPending ? 'Versturen…' : 'Goedkeuren & versturen'}
              </Button>
              <Button variant="ghost" disabled={discardM.isPending} onClick={() => discardM.mutate()}>
                Verwerp
              </Button>
            </div>
          </>
        ) : (
          <pre className={recordDetailShellStyles.bodyPreview}>{message.body_text ?? '—'}</pre>
        )}

        {!isDraft &&
        message.direction === 'inbound' &&
        extractEmailAddress(message.from_addr ?? '') ? (
          replyOpen ? (
            <InboxReplyComposer
              messageId={message.id}
              replyToLabel={
                message.from_addr ?? extractEmailAddress(message.from_addr ?? '') ?? '—'
              }
              originalSubject={message.subject}
              leadId={message.lead_id}
              defaultMailboxId={message.smtp_account_id ?? undefined}
              onCancel={() => onReplyOpenChange(false)}
            />
          ) : (
            <div className={projectStyles.row}>
              <Button variant="primary" onClick={() => onReplyOpenChange(true)}>
                Beantwoorden
              </Button>
            </div>
          )
        ) : null}

        {!message.lead_id && !isDraft ? (
          <div className={projectStyles.row}>
            <TextInput
              placeholder="Lead UUID koppelen"
              value={linkLeadId}
              onChange={(e) => onLinkLeadIdChange(e.target.value)}
            />
            <Button
              onClick={() =>
                patchMsg.mutate({ id: message.id, body: { lead_id: linkLeadId.trim() } })
              }
            >
              Koppelen
            </Button>
          </div>
        ) : null}

        {!isDraft ? (
          <div className={projectStyles.row}>
            <Button
              variant="ghost"
              onClick={() => patchMsg.mutate({ id: message.id, body: { archive: true } })}
            >
              Archiveer
            </Button>
          </div>
        ) : null}
      </RecordDetailShell.Body>
    </RecordDetailShell>
  );
}
